import { useCallback } from 'react'
import { useTolgee, useTranslate as useTolgeeTranslate } from '@tolgee/react'

import type { LocationLanguage, TranslateFunction, TranslateOptions } from './types'
import { toTagParams } from './utils'

export const useTranslate = () => {
  const { t: tolgeeT } = useTolgeeTranslate()
  // @tolgee/react's own `t` is memoized (stable unless tolgee's translation state actually
  // changes) — call sites across the app rely on that stability in their own useMemo/useCallback/
  // useEffect deps (v4's passthrough `t` had it too). Without useCallback here, this wrapper would
  // hand out a new function every render regardless, silently breaking every consumer's
  // memoization downstream.
  const t: TranslateFunction = useCallback(
    (options: TranslateOptions | undefined) => {
      // Call sites across the app conditionally resolve a key entry (e.g. `t(currentAccess?.altLabel)`)
      // and expect a blank result when there isn't one — v4's passthrough `t` tolerated this too.
      if (!options) return ''

      const { key, defaultValue, parameters } = options
      // @tolgee/react's own TFnType types `params` as DefaultParamType (string | number | bigint |
      // Date), narrower than what it actually accepts at runtime — interpolating a ReactElement
      // (e.g. `<strong />`) into a translation is a documented, working Tolgee feature. Our own
      // TranslateOptions.parameters is typed correctly (includes ReactElement); this cast is only
      // bridging that upstream type gap at the call boundary.
      return tolgeeT({ key, defaultValue, params: toTagParams(parameters) } as Parameters<typeof tolgeeT>[0])
    },
    [tolgeeT]
  )
  return Object.assign(t, { t })
}

// Subscribes to the 'language' event so components re-render when the language changes —
// @tolgee/react v6 removed the old useCurrentLanguage/useSetLanguage hooks in favor of the
// single useTolgee(events) hook.
export const useCurrentLanguage = () => {
  const tolgee = useTolgee(['language'])
  return tolgee.getLanguage() as LocationLanguage
}

export const useSetLanguage = () => {
  const tolgee = useTolgee()
  return (language: string) => tolgee.changeLanguage(language)
}

export const useLanguage = () => {
  const language = useCurrentLanguage()
  const setLanguage = useSetLanguage()

  return { language, setLanguage }
}
